import type { DownloadRecord } from './types';

/**
 * Offline playback policy (pure, unit-tested).
 *
 * A download stays playable for OFFLINE_GRACE_DAYS after the server last
 * confirmed the user's access. Every online launch re-verifies (see
 * bootstrap.ts), which pushes lastVerifiedAt forward for active members and
 * flips `entitled` to false the moment a lapsed subscription is detected.
 */
export const OFFLINE_GRACE_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export function isWithinGrace(lastVerifiedAt: string, now: Date = new Date()): boolean {
  const verified = Date.parse(lastVerifiedAt);
  if (!Number.isFinite(verified)) return false;
  return now.getTime() - verified <= OFFLINE_GRACE_DAYS * DAY_MS;
}

export function daysUntilExpiry(lastVerifiedAt: string, now: Date = new Date()): number {
  const verified = Date.parse(lastVerifiedAt);
  if (!Number.isFinite(verified)) return 0;
  const remaining = OFFLINE_GRACE_DAYS * DAY_MS - (now.getTime() - verified);
  return Math.max(0, Math.ceil(remaining / DAY_MS));
}

export type OfflinePlayability =
  | { playable: true }
  | { playable: false; reason: 'not_complete' | 'wrong_user' | 'not_entitled' | 'expired' };

export function getOfflinePlayability(
  record: DownloadRecord | null | undefined,
  userId: string | null | undefined,
  now: Date = new Date(),
): OfflinePlayability {
  if (!record || record.status !== 'complete') return { playable: false, reason: 'not_complete' };
  if (!userId || record.userId !== userId) return { playable: false, reason: 'wrong_user' };
  if (!record.entitled) return { playable: false, reason: 'not_entitled' };
  if (!isWithinGrace(record.lastVerifiedAt, now)) return { playable: false, reason: 'expired' };
  return { playable: true };
}

export function playabilityMessage(p: OfflinePlayability): string | null {
  if (p.playable) return null;
  switch (p.reason) {
    case 'not_complete':
      return 'This video has not finished downloading.';
    case 'wrong_user':
      return 'This download belongs to a different account.';
    case 'not_entitled':
      return 'Your membership no longer includes this lesson. Reconnect to update your downloads.';
    case 'expired':
      return `Downloads need to check in online every ${OFFLINE_GRACE_DAYS} days. Connect to the internet to keep watching.`;
  }
}
